import { api } from '../api';
import {
  Badge,
  BadgeDefinition,
  EnrichedBadge,
  BadgeResponse,
  UserBadgesResponse,
  BadgeDefinitionsResponse,
  BadgeVariant,
} from './types';
import { BadgeTemplate, EnhancedBadgeCriteria, BadgePreview } from '../../types/badge-criteria';
import { BADGE_DEFINITIONS, getBadgeById } from './badgeRegistry';

// Type definitions for badge service
interface PlantWithRelations {
  id: string;
  userId: string;
  displayName: string;
  plantVarietyId?: string | null;
  sourceId?: string | null;
  notes?: string | null;
  plantedDate?: string | null;
  qrCodeId?: string | null;
  labelGeneratedAt?: string | null;
  createdAt: string;
  variety?: {
    id: string;
    speciesId: string;
    primaryCommonName: string;
    alternativeNames: string[];
    plantType?: string | null;
    category?: string | null;
    createdAt: string;
    species?: {
      id: string;
      name: string;
      genusId: string;
      createdAt: string;
      genus?: {
        id: string;
        name: string;
        createdAt: string;
      };
    };
  };
  source?: {
    id: string;
    name: string;
    type: string;
  };
  user?: {
    id: string;
    name?: string;
    email: string;
  };
}

interface UserStatsData {
  accountAge?: number;
  sourcesShared?: number;
}

interface BadgeResponseWithServer {
  id: string;
  name: string;
  description: string;
  icon: string;
  variant: string;
  category: string;
  earnedAt?: string;
  context?: Record<string, unknown>;
  badgeId?: string;
  displayConfig?: {
    icon?: string;
    variant?: BadgeVariant;
  };
}

export class BadgeService {
  // Get all badge definitions from backend
  static async getBadgeDefinitions(): Promise<BadgeDefinitionsResponse> {
    const response = await api.get<{ success: boolean; data: BadgeDefinitionsResponse }>(
      '/badges/definitions'
    );
    return response.data.data;
  }

  // Get badges for a specific plant
  static async getPlantBadges(plantId: string): Promise<BadgeResponse[]> {
    try {
      const response = await api.get<{ success: boolean; data: BadgeResponse[] }>(
        `/badges/plant/${plantId}`
      );
      return response.data.data || [];
    } catch {
      // Silent error handling for plant badges
      // Return empty array instead of throwing to prevent query failures
      return [];
    }
  }

  // Get all badges earned by a user
  static async getUserBadges(userId: string): Promise<UserBadgesResponse> {
    try {
      const response = await api.get<{ success: boolean; data: UserBadgesResponse }>(
        `/badges/user/${userId}`
      );
      return (
        response.data.data || {
          badges: [],
          stats: { total: 0, byCategory: {}, recentlyEarned: [] },
        }
      );
    } catch {
      // Silent error handling for user badges
      // Return empty response instead of throwing to prevent query failures
      return { badges: [], stats: { total: 0, byCategory: {}, recentlyEarned: [] } };
    }
  }

  // Trigger badge evaluation for a user
  static async evaluateUserBadges(userId: string): Promise<void> {
    await api.post(`/badges/evaluate/${userId}`);
  }

  // Admin methods for badge management
  static async getAdminBadgeDefinitions(): Promise<BadgeDefinitionsResponse> {
    const response = await api.get<{ success: boolean; data: BadgeDefinitionsResponse }>(
      '/badges/admin/badges'
    );
    return response.data.data;
  }

  static async createBadgeDefinition(
    definition: Partial<BadgeDefinition>
  ): Promise<BadgeDefinition> {
    const response = await api.post<{ success: boolean; data: BadgeDefinition }>(
      '/badges/admin/badges',
      definition
    );
    return response.data.data;
  }

  static async updateBadgeDefinition(
    id: string,
    updates: Partial<BadgeDefinition>
  ): Promise<BadgeDefinition> {
    const response = await api.put<{ success: boolean; data: BadgeDefinition }>(
      `/badges/admin/badges/${id}`,
      updates
    );
    return response.data.data;
  }

  static async deleteBadgeDefinition(id: string): Promise<void> {
    await api.delete(`/badges/admin/badges/${id}`);
  }

  static async getBadgeAnalytics(params?: Record<string, unknown>): Promise<unknown> {
    const response = await api.get('/badges/admin/badges/analytics', { params });
    return response.data.data;
  }

  // Badge template methods
  static async getBadgeTemplates(): Promise<{ templates: BadgeTemplate[]; total: number }> {
    const response = await api.get('/admin/badge-templates/templates');
    return response.data.data;
  }

  static async createBadgeTemplate(
    template: Omit<BadgeTemplate, 'usage_count'>
  ): Promise<BadgeTemplate> {
    const response = await api.post('/admin/badge-templates/templates', template);
    return response.data.data;
  }

  static async updateBadgeTemplate(
    id: string,
    updates: Partial<BadgeTemplate>
  ): Promise<BadgeTemplate> {
    const response = await api.put(`/admin/badge-templates/templates/${id}`, updates);
    return response.data.data;
  }

  static async deleteBadgeTemplate(id: string): Promise<void> {
    await api.delete(`/admin/badge-templates/templates/${id}`);
  }

  // Badge criteria preview method
  static async previewBadgeCriteria(criteria: EnhancedBadgeCriteria): Promise<BadgePreview> {
    const response = await api.post<{ success: boolean; data: BadgePreview }>(
      '/admin/badges/preview',
      criteria
    );
    return response.data.data;
  }

  // Client-side badge evaluation for plants (when needed for immediate feedback)
  static evaluatePlantBadges(plant: PlantWithRelations): Badge[] {
    return BADGE_DEFINITIONS.filter(
      badge =>
        badge.category === 'plant' &&
        badge.evaluator.type === 'plant' &&
        badge.evaluator.evaluate(plant)
    );
  }

  // Client-side badge evaluation for user collection
  static evaluateCollectionBadges(plants: PlantWithRelations[]): Badge[] {
    return BADGE_DEFINITIONS.filter(
      badge =>
        badge.category === 'achievement' &&
        badge.evaluator.type === 'collection' &&
        badge.evaluator.evaluate(plants)
    );
  }

  // Client-side badge evaluation for user stats
  static evaluateUserStatBadges(userData: UserStatsData): Badge[] {
    return BADGE_DEFINITIONS.filter(
      badge =>
        (badge.category === 'milestone' || badge.category === 'achievement') &&
        badge.evaluator.type === 'user' &&
        badge.evaluator.evaluate(userData)
    );
  }

  // Convert BadgeResponse to EnrichedBadge with local registry data
  static enrichBadgeResponse(badgeResponse: BadgeResponse): EnrichedBadge | null {
    // Try to find by badge ID (the server response includes badgeId field)
    const serverResponse = badgeResponse as BadgeResponseWithServer;
    const badgeId = serverResponse.badgeId || badgeResponse.id;
    const localBadge = getBadgeById(badgeId);

    if (!localBadge) {
      // Badge not found in local registry - create fallback

      // Create a fallback badge from server data
      return {
        id: badgeId,
        name: badgeResponse.name || 'Unknown Badge',
        description: badgeResponse.description || 'Badge description not available',
        icon: serverResponse.displayConfig?.icon || 'StarIcon',
        variant: serverResponse.displayConfig?.variant || 'default',
        category: serverResponse.category || 'achievement',
        evaluator: { type: 'user', evaluate: () => true },
        // Include server response data
        earnedAt: badgeResponse.earnedAt || serverResponse.earnedAt,
        context: badgeResponse.context || serverResponse.context,
      } as EnrichedBadge;
    }

    return {
      ...localBadge,
      // Override with server data if available
      name: badgeResponse.name || localBadge.name,
      description: badgeResponse.description || localBadge.description,
      icon: serverResponse.displayConfig?.icon || localBadge.icon,
      variant: serverResponse.displayConfig?.variant || localBadge.variant,
      // Include server response data
      earnedAt: badgeResponse.earnedAt || serverResponse.earnedAt,
      context: badgeResponse.context || serverResponse.context,
    };
  }

  // Convert array of BadgeResponse to enriched Badge array
  static enrichBadgeResponses(badgeResponses: BadgeResponse[]): EnrichedBadge[] {
    return badgeResponses
      .map(response => this.enrichBadgeResponse(response))
      .filter((badge): badge is EnrichedBadge => badge !== null);
  }

  // Get badge statistics for a collection
  static getBadgeStats(badges: (Badge | EnrichedBadge)[]): {
    total: number;
    byCategory: Record<string, number>;
    plantBadges: (Badge | EnrichedBadge)[];
    achievementBadges: (Badge | EnrichedBadge)[];
    milestoneBadges: (Badge | EnrichedBadge)[];
  } {
    const byCategory: Record<string, number> = {
      plant: 0,
      achievement: 0,
      milestone: 0,
    };

    const plantBadges: (Badge | EnrichedBadge)[] = [];
    const achievementBadges: (Badge | EnrichedBadge)[] = [];
    const milestoneBadges: (Badge | EnrichedBadge)[] = [];

    badges.forEach(badge => {
      byCategory[badge.category]++;

      switch (badge.category) {
        case 'plant':
          plantBadges.push(badge);
          break;
        case 'achievement':
          achievementBadges.push(badge);
          break;
        case 'milestone':
          milestoneBadges.push(badge);
          break;
      }
    });

    return {
      total: badges.length,
      byCategory,
      plantBadges,
      achievementBadges,
      milestoneBadges,
    };
  }

  // Check if a badge should be shown based on plant data
  static shouldShowBadge(badge: Badge, context?: { earnedAt?: string }): boolean {
    // Always show badges that have been earned (from server)
    if (context?.earnedAt) return true;

    // For client-side evaluation, check if badge is active
    return badge.category === 'plant'; // Plant badges are always evaluable client-side
  }

  // Get display variant for badge based on context
  static getBadgeDisplayVariant(badge: Badge, context?: { earnedAt?: string }): BadgeVariant {
    // If badge is earned, use its defined variant
    if (context?.earnedAt) return badge.variant;

    // For preview/potential badges, use muted variant
    return 'default';
  }
}
